import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { resolveImage } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import Seo from "../components/site/Seo";

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [params, setParams] = useSearchParams();
  const activeTag = params.get("tag") || "";
  const { settings } = useSettings();

  useEffect(() => {
    const url = activeTag ? `/blog?tag=${encodeURIComponent(activeTag)}` : "/blog";
    api.get(url).then((r) => setPosts(r.data || [])).catch(() => {});
  }, [activeTag]);

  useEffect(() => {
    api.get("/blog-tags").then((r) => setTags(r.data || [])).catch(() => {});
  }, []);

  const setTag = (t) => {
    if (!t) setParams({});
    else setParams({ tag: t });
  };

  return (
    <div data-testid="blog-page">
      <Seo
        title="Blog — Reptile Global"
        description="Insights, product updates and engineering deep dives from Reptile Global."
        organizationSchema={settings?.organization_schema}
      />
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-3">Blog</div>
          <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter text-zinc-950">Notes from the Reptile team.</h1>

          {tags.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2" data-testid="blog-tags">
              <button
                onClick={() => setTag("")}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition ${
                  !activeTag ? "bg-orange-500 text-white border-orange-500" : "border-zinc-200 text-zinc-700 hover:border-orange-500"
                }`}
                data-testid="blog-tag-all"
              >
                All
              </button>
              {tags.map((t) => (
                <button
                  key={t.tag}
                  onClick={() => setTag(t.tag)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition ${
                    activeTag === t.tag ? "bg-orange-500 text-white border-orange-500" : "border-zinc-200 text-zinc-700 hover:border-orange-500"
                  }`}
                  data-testid={`blog-tag-${t.tag}`}
                >
                  #{t.tag} <span className="opacity-60">({t.count})</span>
                </button>
              ))}
            </div>
          )}

          {posts.length === 0 ? (
            <div className="mt-16 bg-white border border-zinc-200 rounded-2xl p-12 text-center">
              <p className="text-zinc-500">No posts {activeTag ? `tagged "#${activeTag}"` : "published yet"}. Check back soon.</p>
            </div>
          ) : (
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group bg-white border border-zinc-200 rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all"
                >
                  {p.cover_image && (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img src={resolveImage(p.cover_image)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="text-xs text-zinc-500 mb-2">{new Date(p.created_at).toLocaleDateString()}</div>
                    <div className="font-display font-bold text-xl text-zinc-900 group-hover:text-orange-600 transition mb-2">{p.title}</div>
                    <p className="text-sm text-zinc-600 line-clamp-3">{p.excerpt}</p>
                    {(p.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {p.tags.slice(0, 3).map((t) => (
                          <span key={t} className="text-xs bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">#{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
